import { Instant, LocalDate, LocalDateTime, LocalTime, Month } from "@js-joda/core";
import { isDefined } from "../components/Generic/IsDefined";
import {
  AddressComponents,
  PatientDocumentTypeId,
  PatientTaskBroadcastDetails,
  PatientTaskInstanceDocument,
  VisitInstanceDetails,
} from "../messages";
import { STRINGS } from "../strings";
import { Patient } from "../system/AppState/AppPatientsState";
import { PatientTaskInstance } from "../system/AppState/AppTasksState";
import { globalize, translate } from "../translate";
import { fmap } from "./fmap";

export function getTaskDatesRangeText(startDate: LocalDate, dueDate: LocalDate) {
  const startDateFormatted = formatLocalDateToMedium(startDate);
  const dueDateFormatted = formatLocalDateToMedium(dueDate);

  const currentYear = LocalDate.now().year();
  const isSameYearsForDates = startDate.year() === dueDate.year();

  const getYearString = (year: number) => {
    const isCurrentYear = year === currentYear;
    return isCurrentYear && isSameYearsForDates ? "" : ` ${year}`;
  };

  let taskStartDateYear = getYearString(startDate.year());
  let taskDueDateYear = getYearString(dueDate.year());

  const startsCurrentYear = startDate.year() === currentYear;
  const endsNextYear = dueDate.year() === currentYear + 1;

  if (startsCurrentYear && endsNextYear) {
    taskStartDateYear = "";
    taskDueDateYear = ` (${translate(STRINGS.NEXT_YEAR)})`;
  }

  const taskDatesRangeText = translate(STRINGS.DATES_RANGE_TO_COMPLETE_TASKS_FROM_TO)
    .replace("%start%", startDateFormatted + taskStartDateYear)
    .replace("%end%", dueDateFormatted + taskDueDateYear);

  return taskDatesRangeText;
}

export function formatDateRange(startDateLocal: LocalDate, endDateLocal: LocalDate) {
  const format = (date: LocalDate) =>
    globalize().formatLocalDate(
      date,
      startDateLocal.year() === endDateLocal.year() ? { skeleton: "MMMd" } : { date: "short" }
    );

  const startDate = format(startDateLocal);
  const endDate = format(endDateLocal);

  return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
}

function formatLocalDateToMedium(date: LocalDate) {
  return globalize().formatLocalDate(date, { date: "medium" }).split(",")[0];
}

export function formatTaskAddress({
  addressString,
  addressComponents,
}: {
  addressString: string | null;
  addressComponents: AddressComponents | null;
}): string | null {
  if (addressString !== null) {
    return addressString;
  }

  if (addressComponents !== null) {
    if (addressComponents.formatedAddressDetails !== undefined) {
      return addressComponents.formatedAddressDetails.fullAddress;
    }

    return [
      addressComponents.sublocalityLevel1,
      addressComponents.neighborhood,
      addressComponents.locality,
    ]
      .filter(Boolean)
      .join(", ");
  }

  return null;
}

export const scheduleOptions = ["Morning", "Afternoon", "Evening", "Night"] as const;
export type Schedule = (typeof scheduleOptions)[number];

export function getScheduleFromTime(time: LocalTime): Schedule {
  if (time.isBefore(LocalTime.parse("04:00"))) {
    return "Night";
  } else if (time.isBefore(LocalTime.parse("12:00"))) {
    return "Morning";
  } else if (time.isBefore(LocalTime.parse("17:00"))) {
    return "Afternoon";
  } else if (time.isBefore(LocalTime.parse("21:00"))) {
    return "Evening";
  } else {
    return "Night";
  }
}

export function getResubmissionReasonFromDocuments(
  documents: PatientTaskInstanceDocument[],
  documentId: PatientDocumentTypeId
): string | null {
  const resubmissionReason =
    documents.find(
      (doc): doc is PatientTaskInstanceDocument.PatientTaskInstanceDocumentRegular =>
        doc.type === "PatientTaskInstanceDocumentRegular" &&
        doc.isOpenForResubmission &&
        doc.id === documentId
    )?.resubmissionReason ?? null;

  return resubmissionReason;
}

export function getTasksWithRequiredFixes(tasks: PatientTaskInstance[]) {
  if (tasks.length === 0) return [];

  const tasksWithRequiredFixes = tasks
    .filter((task: PatientTaskInstance | PatientTaskBroadcastDetails) => isTaskFixesRequired(task))
    .map(({ scheduleDateTime }) => scheduleDateTime)
    .filter(isDefined)
    .map((scheduleDateTime) => {
      const taskDate = LocalDate.ofInstant(scheduleDateTime);
      return {
        year: taskDate.year(),
        month: taskDate.month().value(),
      };
    })
    .sort((a: { year: number; month: number }, b: { year: number; month: number }) => {
      // Sort by date descending (most recent first)
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });

  return tasksWithRequiredFixes;
}

export function isTaskFixesRequired(task: PatientTaskInstance | PatientTaskBroadcastDetails) {
  if ("taskDetails" in task) {
    return task.taskDetails.documents.some(
      (doc) => doc.type === "PatientTaskInstanceDocumentRegular" && doc.isOpenForResubmission
    );
  }
  return task.documents.some(
    (doc) => doc.type === "PatientTaskInstanceDocumentRegular" && doc.isOpenForResubmission
  );
}

export function isTaskScheduledInMonth(
  task: PatientTaskInstance | PatientTaskBroadcastDetails,
  filters: {
    scheduleYear?: number;
    scheduleMonth?: Month;
  }
) {
  if ("scheduleDateTime" in task && task.scheduleDateTime) {
    const taskDate = LocalDate.ofInstant(task.scheduleDateTime);
    return taskDate.month() == filters.scheduleMonth && taskDate.year() == filters.scheduleYear;
  }
  return false;
}

export function filterPatientTask(
  task: PatientTaskInstance,
  filters: {
    patient: Patient | null;
    boroughs: string[];
    patientSchedule: string[];
    visitTypes: string[];
    isFixesRequiredOnly: boolean;
    scheduleYear?: number;
    scheduleMonth?: Month;
  }
) {
  if (filters.patient && task.taskDetails.patientId !== filters.patient.id) return false;

  if (
    filters.boroughs.length > 0 &&
    !filters.boroughs.includes(task.taskDetails.patientAddressComponent?.sublocalityLevel1 ?? "")
  )
    return false;

  const taskSchedule = fmap(task.scheduleDateTime, (x) =>
    getScheduleFromTime(LocalDateTime.ofInstant(x).toLocalTime())
  );

  if (
    filters.patientSchedule.length > 0 &&
    (taskSchedule === null || !filters.patientSchedule.includes(taskSchedule))
  )
    return false;

  if (filters.visitTypes.length > 0 && !filters.visitTypes.includes(task.taskDetails.title))
    return false;

  if (
    filters.isFixesRequiredOnly &&
    isTaskScheduledInMonth(task, filters) &&
    !isTaskFixesRequired(task)
  )
    return false;

  return true;
}

export function filterPatientTaskBroadcast(
  task: PatientTaskBroadcastDetails,
  filters: {
    patient: Patient | null;
    boroughs: string[];
    patientSchedule: string[];
    visitTypes: string[];
    isFixesRequiredOnly: boolean;
  }
) {
  if (filters.patient && task.patientId !== filters.patient.id) return false;

  if (
    filters.boroughs.length > 0 &&
    !filters.boroughs.includes(task.patientAddressComponents?.sublocalityLevel1 ?? "")
  )
    return false;

  // Task is not scheduled yet
  if (filters.patientSchedule.length > 0) return false;

  if (filters.visitTypes.length > 0 && !filters.visitTypes.includes(task.title)) return false;

  if (filters.isFixesRequiredOnly && !isTaskFixesRequired(task)) return false;

  return true;
}

export type AssignedTaskStatus = "SCHEDULED" | "ASSIGNED";

export function getAssignedTaskStatus(task: PatientTaskInstance): AssignedTaskStatus {
  switch (task.type) {
    case "COMPLETED":
    case "SCHEDULED":
      return "SCHEDULED";

    case "PENDING":
      return "ASSIGNED";
  }
}

export const getIsTaskCompleted = (
  scheduledVisit: VisitInstanceDetails,
  task: "CLOCK_IN" | "SUBMIT_DUTIES" | "CLOCK_OUT"
): boolean => {
  switch (task) {
    case "CLOCK_IN":
      return isDefined(scheduledVisit.clockInTime) || scheduledVisit.checkInTime !== null;

    case "CLOCK_OUT":
      return isDefined(scheduledVisit.clockOutTime) || scheduledVisit.checkOutTime !== null;

    case "SUBMIT_DUTIES":
      return scheduledVisit.isDutySheetCompleted;
  }
};

export function getHasTaskPassed(task: PatientTaskInstance) {
  switch (task.type) {
    case "COMPLETED":
    case "SCHEDULED":
      return task.scheduleDateTime.isBefore(Instant.now());

    case "PENDING":
      return false;
  }
}

function getDateRangeText(startDate: LocalDate, endDate: LocalDate) {
  if (startDate.year() === endDate.year() && startDate.monthValue() === endDate.monthValue()) {
    const from = globalize().formatLocalDate(startDate, { skeleton: "MMMd" });
    const to = globalize().formatLocalDate(endDate, { skeleton: "d" });

    return startDate.dayOfMonth() === endDate.dayOfMonth() ? from : `${from} - ${to}`;
  }

  if (startDate.year() === endDate.year()) {
    const from = globalize().formatLocalDate(startDate, { skeleton: "MMMd" });
    const to = globalize().formatLocalDate(endDate, { skeleton: "MMMd" });

    return `${from} - ${to}`;
  }

  const from = globalize().formatLocalDate(startDate, { date: "medium" });
  const to = globalize().formatLocalDate(endDate, { date: "medium" });

  return `${from} - ${to}`;
}

export function getAssignedTaskDateText(task: PatientTaskInstance) {
  switch (task.type) {
    case "COMPLETED":
    case "SCHEDULED": {
      const localDate = LocalDateTime.ofInstant(task.scheduleDateTime).toLocalDate();
      return globalize().formatLocalDate(localDate, { date: "medium" });
    }

    case "PENDING":
      return getDateRangeText(task.startDate, task.dueDate);
  }
}

export function getBroadcastTaskDateText(task: PatientTaskBroadcastDetails) {
  return getDateRangeText(task.startDate, task.dueDate);
}

export function onTaskScheduleIsAlreadyTakenError(err: any) {
  let errorBody: any | null = null;
  try {
    errorBody = JSON.parse(err.body);
  } catch (e) {
    console.error(e);
    return false;
  }

  return errorBody.resource === "TASK" && errorBody.status === "ALREADY_ASSIGNED";
}
